import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AgentSearchSelect from '../../components/form/AgentSearchSelect';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { getApiErrorMessage } from '../../utils/errors';
import { listInstagramAccounts } from '../../api/instagramAccounts';
import { listWhatsappCatalogAdmin } from '../../api/whatsappCatalogAdmin';
import {
  createChannelAdmin,
  deleteChannelAdmin,
  getChannelAdmin,
  listChannelsAdmin,
  rotateChannelWebhookSecretAdmin,
  updateChannelAdmin,
} from '../../api/channelsAdmin';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const WEBHOOK_URL = `${API_BASE_URL.replace(/\/$/, '')}/webhook`;

const emptyDetail = {
  id: '',
  name: '',
  type: '',
  status: '',
  agentName: '',
  accountName: '',
  accountEmail: '',
  botmakerChannelId: '',
  phoneNumber: '',
  igUserId: '',
  catalogLabel: '',
  catalogSlug: '',
  provider: '',
  deliveryUrl: '',
  deliveryToken: '',
  deliveryTokenConfigured: false,
  publicId: '',
  inboundWebhookSecret: '',
};

const emptyCreateForm = {
  agentId: '',
  name: '',
  type: 'whatsapp',
  catalogId: '',
  deliveryUrl: '',
  deliveryToken: '',
};

const TYPE_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'web', label: 'Web / PWA' },
];

function isValidInstagramUserId(value) {
  if (!value) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== '' && normalized !== 'pending' && normalized !== '0';
}

function mapChannelToDetail(channel) {
  return {
    id: channel?.id || '',
    name: channel?.name || '',
    type: channel?.type || '',
    status: channel?.status || '',
    agentName: channel?.agent?.name || '',
    accountName: channel?.account?.name || '',
    accountEmail: channel?.account?.email || '',
    botmakerChannelId: channel?.botmaker_channel_id || '',
    phoneNumber: channel?.phone_number || '',
    igUserId: channel?.ig_user_id || '',
    catalogLabel: channel?.catalog_label || '',
    catalogSlug: channel?.catalog_slug || '',
    provider: channel?.provider || '',
    deliveryUrl: channel?.configurations?.delivery_url || '',
    deliveryToken: '',
    deliveryTokenConfigured: Boolean(
      channel?.configurations?.delivery_token_configured
    ),
    publicId: channel?.configurations?.public_id || channel?.id || '',
    inboundWebhookSecret: channel?.inbound_webhook_secret || '',
  };
}

function channelListSubtitle(entry) {
  if (entry.type === 'instagram') {
    const ig = entry.ig_user_id || 'sem ig user id';
    const label = entry.catalog_label || entry.catalog_slug;
    return label ? `${label} · ${ig}` : ig;
  }
  if (entry.type === 'web') {
    return entry.web?.delivery_url || 'sem URL de entrega';
  }
  return `${entry.account?.name || '—'} · ${entry.phone_number || 'sem telefone'}`;
}

function AdminChannels() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [whatsappSlots, setWhatsappSlots] = useState([]);
  const [instagramSlots, setInstagramSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [detail, setDetail] = useState(emptyDetail);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [filterBotmakerId, setFilterBotmakerId] = useState(
    searchParams.get('botmaker_channel_id') || ''
  );

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listChannelsAdmin({
        page: 1,
        perPage: 100,
        botmakerChannelId: filterBotmakerId.trim() || undefined,
      });
      setEntries(response.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os canais.'));
    } finally {
      setLoading(false);
    }
  }, [filterBotmakerId]);

  const loadCatalogSlots = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [whatsappResponse, instagramResponse] = await Promise.all([
        listWhatsappCatalogAdmin().catch(() => ({ data: [] })),
        listInstagramAccounts().catch(() => ({ data: [] })),
      ]);

      const waSlots = (whatsappResponse.data || []).filter((slot) => {
        if (!slot.is_active || !slot.token_configured) return false;
        if (slot.provider === 'meta') {
          return Boolean(slot.phone_number_id);
        }
        return Boolean(slot.botmaker_channel_id);
      });
      setWhatsappSlots(waSlots);

      const igSlots = (instagramResponse.data || []).filter(
        (slot) =>
          slot.is_active &&
          slot.token_configured &&
          isValidInstagramUserId(slot.ig_user_id)
      );
      setInstagramSlots(igSlots);
    } catch {
      setWhatsappSlots([]);
      setInstagramSlots([]);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadCatalogSlots();
  }, [loadCatalogSlots]);

  const activeSlots = useMemo(() => {
    if (createForm.type === 'instagram') return instagramSlots;
    if (createForm.type === 'web') return [];
    return whatsappSlots;
  }, [createForm.type, instagramSlots, whatsappSlots]);

  const catalogOptions = useMemo(() => {
    const placeholder =
      createForm.type === 'instagram'
        ? 'Selecione um slot do catálogo Instagram'
        : 'Selecione um slot do catálogo WhatsApp';

    const options = activeSlots.map((slot) => {
      if (createForm.type === 'instagram') {
        const handle = slot.username ? `@${slot.username}` : slot.ig_user_id;
        return {
          value: slot.id,
          label: `${slot.label} · ${handle}`,
        };
      }
      const providerLabel = slot.provider === 'meta' ? 'Meta' : 'Botmaker';
      const identifier =
        slot.phone_number ||
        (slot.provider === 'meta' ? slot.phone_number_id : slot.botmaker_channel_id) ||
        slot.slug;
      return {
        value: slot.id,
        label: `${slot.label} · ${providerLabel} · ${identifier}`,
      };
    });

    return [{ value: '', label: placeholder }, ...options];
  }, [activeSlots, createForm.type]);

  const handleSelect = async (entry) => {
    setError('');
    try {
      const channel = await getChannelAdmin(entry.id);
      setDetail(mapChannelToDetail(channel));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o canal.'));
    }
  };

  const handleApplyFilter = (event) => {
    event.preventDefault();
    const value = filterBotmakerId.trim();
    if (value) {
      setSearchParams({ botmaker_channel_id: value });
    } else {
      setSearchParams({});
    }
  };

  const handleCreateChannel = async (event) => {
    event.preventDefault();
    setFormError('');
    setSuccess('');

    if (!createForm.agentId) {
      setFormError('Selecione um agente.');
      return;
    }
    if (!createForm.name.trim()) {
      setFormError('Informe o nome do canal.');
      return;
    }
    if (createForm.type !== 'web' && !createForm.catalogId) {
      setFormError(
        createForm.type === 'instagram'
          ? 'Selecione um slot do catálogo Instagram.'
          : 'Selecione um slot do catálogo WhatsApp.'
      );
      return;
    }
    if (createForm.type === 'web' && !createForm.deliveryUrl.trim()) {
      setFormError('Informe a URL HTTPS que receberá as mensagens do Bot Forge.');
      return;
    }
    if (createForm.type === 'web' && !createForm.deliveryToken.trim()) {
      setFormError('Informe o segredo que autentica o Bot Forge no PWA.');
      return;
    }

    setSaving(true);
    try {
      const channel = await createChannelAdmin({
        agentId: createForm.agentId,
        name: createForm.name.trim(),
        type: createForm.type,
        catalogId: createForm.catalogId,
        deliveryUrl: createForm.deliveryUrl.trim(),
        deliveryToken: createForm.deliveryToken,
      });
      setCreateForm(emptyCreateForm);
      setSuccess('Canal criado com sucesso.');
      await loadEntries();
      setDetail(mapChannelToDetail(channel));
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Não foi possível criar o canal.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWebChannel = async (event) => {
    event.preventDefault();
    if (!detail.id || detail.type !== 'web') return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const channel = await updateChannelAdmin(detail.id, {
        name: detail.name.trim(),
        deliveryUrl: detail.deliveryUrl.trim(),
        deliveryToken: detail.deliveryToken,
      });
      setDetail(mapChannelToDetail(channel));
      setSuccess('Canal web atualizado com sucesso.');
      await loadEntries();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível atualizar o canal web.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRotateInboundSecret = async () => {
    if (!detail.id || detail.type !== 'web') return;
    setSaving(true);
    setError('');
    try {
      const result = await rotateChannelWebhookSecretAdmin(detail.id);
      setDetail((prev) => ({
        ...prev,
        inboundWebhookSecret: result.inbound_webhook_secret || '',
      }));
      setSuccess('Novo segredo gerado. Copie agora para o PWA.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível gerar um novo segredo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChannel = async () => {
    if (!detail.id) return;

    const confirmed = window.confirm(
      `Desativar o canal "${detail.name}" do agente ${detail.agentName}? O vínculo deixará de receber mensagens.`
    );
    if (!confirmed) return;

    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      await deleteChannelAdmin(detail.id);
      setDetail(emptyDetail);
      setSuccess('Canal desativado com sucesso.');
      await loadEntries();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível desativar o canal.'));
    } finally {
      setDeleting(false);
    }
  };

  const isInstagramDetail = detail.type === 'instagram';
  const isWebDetail = detail.type === 'web';
  const isMetaWhatsappDetail =
    detail.type === 'whatsapp' && detail.provider === 'meta';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Canais</h1>
        <p className="text-sm text-content-muted mt-1">
          Configure WhatsApp, Instagram ou uma integração HTTP com um PWA.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <form onSubmit={handleCreateChannel} className="surface-card p-6 shadow-card-md space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Criar canal</h2>
          <p className="text-sm text-content-muted mt-1">
            Vincule um agente a um provedor ou à URL HTTPS do seu aplicativo.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <AgentSearchSelect
            id="create_channel_agent"
            label="Agente"
            value={createForm.agentId}
            onChange={(event) =>
              setCreateForm((prev) => ({ ...prev, agentId: event.target.value }))
            }
            mode="admin"
            showAllOption={false}
            layout="stacked"
            disabled={saving}
          />

          <Input
            id="create_channel_name"
            label="Nome do canal"
            value={createForm.name}
            onChange={(event) =>
              setCreateForm((prev) => ({ ...prev, name: event.target.value }))
            }
            placeholder={
              createForm.type === 'instagram'
                ? 'Instagram Vendas'
                : createForm.type === 'web'
                  ? 'Aplicativo de nutrição'
                  : 'WhatsApp Vendas'
            }
            required
          />
        </div>

        <Select
          id="create_channel_type"
          label="Tipo"
          value={createForm.type}
          onChange={(event) =>
            setCreateForm((prev) => ({
              ...prev,
              type: event.target.value,
              catalogId: '',
              deliveryUrl: '',
              deliveryToken: '',
            }))
          }
          options={TYPE_OPTIONS}
          required
        />

        {createForm.type === 'web' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              id="create_channel_delivery_url"
              label="URL de entrega do PWA"
              type="url"
              value={createForm.deliveryUrl}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, deliveryUrl: event.target.value }))
              }
              placeholder="https://app.exemplo.com/api/botforge/messages"
              hint="O Bot Forge enviará cada resposta em JSON para esta URL."
              required
            />
            <Input
              id="create_channel_delivery_token"
              label="Segredo de entrega"
              type="password"
              value={createForm.deliveryToken}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, deliveryToken: event.target.value }))
              }
              autoComplete="new-password"
              hint="Segredo definido no PWA para autenticar os POSTs do Bot Forge."
              required
            />
          </div>
        ) : (
          <Select
            id="create_channel_catalog"
            label={
              createForm.type === 'instagram'
                ? 'Slot do catálogo Instagram'
                : 'Slot do catálogo WhatsApp'
            }
            value={createForm.catalogId}
            onChange={(event) =>
              setCreateForm((prev) => ({ ...prev, catalogId: event.target.value }))
            }
            options={catalogOptions}
            required
            hint={
              catalogLoading
                ? 'Carregando slots...'
                : activeSlots.length === 0
                  ? createForm.type === 'instagram'
                    ? 'Nenhum slot ativo configurado. Cadastre em Catálogo Instagram.'
                    : 'Nenhum slot ativo configurado. Cadastre em Catálogo WhatsApp.'
                  : createForm.type === 'instagram'
                    ? 'Slots ativos com token e Instagram user ID.'
                    : 'Slots ativos com token (Botmaker ou Meta).'
            }
          />
        )}

        {formError && <p className="text-sm text-red-600">{formError}</p>}
        {success && <p className="text-sm text-emerald-700">{success}</p>}

        <div>
          <button
            type="submit"
            disabled={saving || (createForm.type !== 'web' && catalogLoading)}
            className="btn-primary text-sm disabled:opacity-40"
          >
            {saving ? 'Criando...' : 'Criar canal'}
          </button>
        </div>
      </form>

      <form onSubmit={handleApplyFilter} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <Input
            id="filter_botmaker_channel_id"
            label="Filtrar por Botmaker channel ID"
            value={filterBotmakerId}
            onChange={(e) => setFilterBotmakerId(e.target.value)}
            placeholder="bm-shared-123"
          />
        </div>
        <button type="submit" className="btn-secondary text-sm">
          Filtrar
        </button>
      </form>

      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <section className="surface-card p-6 shadow-card-md space-y-4">
          <h2 className="text-lg font-semibold text-content-primary">Canais ativos</h2>
          {loading ? (
            <p className="text-sm text-content-muted">Carregando...</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-content-muted">Nenhum canal encontrado.</p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(entry)}
                    className={`w-full py-3 text-left transition-colors ${
                      detail.id === entry.id
                        ? 'text-forge-700'
                        : 'hover:text-forge-600'
                    }`}
                  >
                    <p className="font-medium text-content-primary">
                      {entry.agent?.name || 'Agente'}
                    </p>
                    <p className="text-xs text-content-muted mt-0.5">
                      {entry.type || '—'} · {channelListSubtitle(entry)}
                    </p>
                    {entry.type === 'whatsapp' && (
                      <p className="text-xs text-content-subtle mt-0.5 truncate">
                        {entry.botmaker_channel_id || 'sem botmaker id'}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="surface-card p-6 shadow-card-md">
          <h2 className="text-lg font-semibold text-content-primary mb-4">
            {detail.id ? `Canal: ${detail.agentName}` : 'Selecione um canal'}
          </h2>

          {!detail.id ? (
            <p className="text-sm text-content-muted">
              Escolha um canal na lista para ver os detalhes.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  id="channel_name"
                  label="Nome do canal"
                  value={detail.name}
                  onChange={(event) =>
                    setDetail((prev) => ({ ...prev, name: event.target.value }))
                  }
                  disabled={!isWebDetail}
                />
                <Input id="channel_type" label="Tipo" value={detail.type} disabled />
              </div>

              <Input id="agent_name" label="Agente" value={detail.agentName} disabled />

              <Input
                id="account_name"
                label="Conta"
                value={
                  detail.accountEmail
                    ? `${detail.accountName} (${detail.accountEmail})`
                    : detail.accountName
                }
                disabled
              />

              {isWebDetail ? (
                <div className="space-y-4 rounded-lg border border-slate-200 p-4">
                  <Input
                    id="web_delivery_url"
                    label="URL de entrega do PWA"
                    type="url"
                    value={detail.deliveryUrl}
                    onChange={(event) =>
                      setDetail((prev) => ({ ...prev, deliveryUrl: event.target.value }))
                    }
                    required
                  />
                  <Input
                    id="web_delivery_token"
                    label="Novo segredo de entrega (opcional)"
                    type="password"
                    value={detail.deliveryToken}
                    onChange={(event) =>
                      setDetail((prev) => ({ ...prev, deliveryToken: event.target.value }))
                    }
                    placeholder={
                      detail.deliveryTokenConfigured
                        ? 'Deixe vazio para manter o segredo atual'
                        : 'Informe o segredo esperado pelo PWA'
                    }
                    autoComplete="new-password"
                  />
                  <Input
                    id="web_inbound_url"
                    label="Webhook do Bot Forge"
                    value={WEBHOOK_URL}
                    disabled
                  />
                  <Input
                    id="web_public_id"
                    label="channel_id para o PWA"
                    value={detail.publicId}
                    disabled
                  />
                  {detail.inboundWebhookSecret && (
                    <Input
                      id="web_inbound_secret"
                      label="Segredo inbound (copie agora)"
                      value={detail.inboundWebhookSecret}
                      disabled
                    />
                  )}
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleUpdateWebChannel}
                      disabled={saving}
                      className="btn-primary text-sm disabled:opacity-40"
                    >
                      {saving ? 'Salvando...' : 'Salvar canal web'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRotateInboundSecret}
                      disabled={saving}
                      className="btn-secondary text-sm disabled:opacity-40"
                    >
                      Gerar novo segredo inbound
                    </button>
                  </div>
                  <p className="text-xs text-content-muted">
                    O PWA envia mensagens para o webhook com o channel_id e o
                    segredo inbound via Authorization: Bearer.
                  </p>
                </div>
              ) : isInstagramDetail ? (
                <>
                  <Input
                    id="ig_user_id"
                    label="Instagram user ID"
                    value={detail.igUserId || '—'}
                    disabled
                  />
                  <Input
                    id="catalog_label"
                    label="Slot do catálogo"
                    value={
                      detail.catalogLabel
                        ? `${detail.catalogLabel}${
                            detail.catalogSlug ? ` (${detail.catalogSlug})` : ''
                          }`
                        : detail.catalogSlug || '—'
                    }
                    disabled
                  />
                </>
              ) : (
                <>
                  <Input
                    id="botmaker_channel_id"
                    label={isMetaWhatsappDetail ? 'Origem' : 'Botmaker channel ID'}
                    value={
                      isMetaWhatsappDetail
                        ? 'Meta Cloud API'
                        : detail.botmakerChannelId || '—'
                    }
                    disabled
                  />
                  <Input
                    id="phone_number"
                    label="Telefone"
                    value={detail.phoneNumber || '—'}
                    disabled
                  />
                </>
              )}

              <div className="pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handleDeleteChannel}
                  disabled={deleting}
                  className="text-sm text-red-600 hover:text-red-700 disabled:opacity-40"
                >
                  {deleting ? 'Desativando...' : 'Desativar canal'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AdminChannels;
